import React, { useEffect, useState } from 'react';
import { getTournamentUserRanking, UserStatistics } from '../../services/statisticsService';
import { formatDistance, formatTime, formatPace, formatDate } from '../../utils/formatters';
import { FaUsers, FaMedal, FaTrophy, FaSpinner, FaExclamationTriangle, FaCrown, FaRunning, FaStopwatch, FaSearch } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import UserActivitiesDialog from '../../components/UserActivitiesDialog';

interface TournamentUserRankingProps {
  tournamentId: number;
  startDateLocal: string;
  endDate: string;
}

const TournamentUserRanking: React.FC<TournamentUserRankingProps> = ({ tournamentId, startDateLocal, endDate }) => {
  const { theme } = useTheme();
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userRanking, setUserRanking] = useState<UserStatistics[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [selectedUserName, setSelectedUserName] = useState<string>('');
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchUserRanking = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getTournamentUserRanking(tournamentId, startDateLocal, endDate);
        setUserRanking(data);
      } catch (err: any) {
        setError(err.message || 'Có lỗi xảy ra khi lấy bảng xếp hạng người dùng trong giải đấu');
      } finally {
        setLoading(false);
      }
    };

    if (tournamentId) {
      fetchUserRanking();
    }
  }, [tournamentId, startDateLocal, endDate]);

  const handleOpenUserActivities = (userId: number, userName: string) => {
    setSelectedUserId(userId);
    setSelectedUserName(userName);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedUserId(null);
    setSelectedUserName('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  if (loading) {
    return (
      <div className="loading-state">
        <FaSpinner className="icon-spin" size={40} color="#20757A" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <FaExclamationTriangle size={20} />
        <span>{error}</span>
      </div>
    );
  }

  if (!userRanking || userRanking.length === 0) {
    return (
      <div className="empty-state">
        <div className="empty-state-icon">
          <FaUsers />
        </div>
        <div className="empty-state-text">Không có dữ liệu xếp hạng người dùng cho giải đấu này</div>
      </div>
    );
  }

  // Sắp xếp theo quãng đường giảm dần
  const sortedRanking = [...userRanking].sort((a, b) => b.totalDistance - a.totalDistance);

  // Lọc danh sách theo từ khóa tìm kiếm
  const filteredRanking = searchTerm.trim() !== '' 
    ? sortedRanking.filter(user => 
        user.fullName.toLowerCase().includes(searchTerm.toLowerCase()))
    : sortedRanking;

  // Hiển thị top 3 người dùng (không bị ảnh hưởng bởi tìm kiếm)
  const top3Users = sortedRanking.slice(0, 3);

  return (
    <div className="tournament-user-ranking">
      {top3Users.length > 0 && searchTerm.trim() === '' && (
        <div className={`statistics-card ${theme}`}>
          <div className="statistics-card-header">
            <h2 className="statistics-card-title">
              <FaCrown /> Top cá nhân xuất sắc
            </h2>
          </div>
          
          <div className="top3-users">
            {top3Users.map((user, index) => (
              <div 
                key={user.userId} 
                className={`top-user-podium top-${index + 1}`}
                onClick={() => handleOpenUserActivities(user.userId, user.fullName)}
                style={{ cursor: 'pointer' }}
              >
                <div className="medal-icon">
                  {index === 0 && <FaMedal color="#FFD700" size={24} />}
                  {index === 1 && <FaMedal color="#C0C0C0" size={24} />}
                  {index === 2 && <FaMedal color="#CD7F32" size={24} />}
                </div>
                
                {user.avatarUrl ? (
                  <img 
                    src={user.avatarUrl} 
                    alt={user.fullName}
                    className="top-user-avatar"
                  />
                ) : (
                  <div className="top-user-avatar-placeholder">
                    {user.fullName.charAt(0).toUpperCase()}
                  </div>
                )}
                
                <div className="top-user-name">{user.fullName}</div>
                <div className="top-user-distance">
                  <FaRunning size={12} style={{ marginRight: 5 }} /> 
                  {formatDistance(user.totalDistance)}
                </div>
                <div className="top-user-activities">
                  <FaStopwatch size={12} style={{ marginRight: 5 }} />
                  {user.activityCount} hoạt động
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className={`statistics-card ${theme}`}>
        <div className="statistics-card-header">
          <h2 className="statistics-card-title">
            <FaUsers /> Bảng xếp hạng người tham gia
          </h2>
        </div>
        
        <div className="search-box">
          <div className="search-form">
            <input
              type="text"
              className="search-input"
              placeholder="Tìm kiếm theo tên người dùng..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <div className="search-actions">
              <FaSearch className="search-icon" />
            </div>
          </div>
        </div>
        
        <table className="ranking-table">
          <thead>
            <tr>
              <th>Thứ hạng</th>
              <th>Người tham gia</th>
              <th>Quãng đường</th>
              <th>Hoạt động</th>
              <th>Thời gian</th>
              <th>Hoạt động gần nhất</th>
            </tr>
          </thead>
          <tbody>
            {filteredRanking.map((user, index) => {
              const originalIndex = sortedRanking.findIndex(u => u.userId === user.userId);
              return (
                <tr 
                  key={user.userId} 
                  className={originalIndex < 3 ? `top-${originalIndex + 1}-row member-row` : 'member-row'}
                  onClick={() => handleOpenUserActivities(user.userId, user.fullName)}
                  style={{ cursor: 'pointer' }}
                >
                  <td className="rank-cell">
                    {originalIndex === 0 && <FaMedal color="#FFD700" size={16} style={{ marginRight: 5 }} />}
                    {originalIndex === 1 && <FaMedal color="#C0C0C0" size={16} style={{ marginRight: 5 }} />}
                    {originalIndex === 2 && <FaMedal color="#CD7F32" size={16} style={{ marginRight: 5 }} />}
                    #{originalIndex + 1}
                  </td>
                  <td>
                    <div className="user-info">
                      {user.avatarUrl ? (
                        <img 
                          src={user.avatarUrl} 
                          alt={user.fullName}
                          className="user-avatar"
                        />
                      ) : (
                        <div className="user-avatar-placeholder">
                          {user.fullName.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="user-name">{user.fullName}</span>
                    </div>
                  </td>
                  <td className="distance-cell">{formatDistance(user.totalDistance)}</td>
                  <td className="activity-count-cell">{user.activityCount}</td>
                  <td>{formatTime(user.totalMovingTime)}</td>
                  <td>{formatDate(user.lastActivityDate)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredRanking.length === 0 && (
          <div className="empty-state" style={{ padding: '30px 0' }}>
            <div className="empty-state-icon">
              <FaSearch />
            </div>
            <div className="empty-state-text">Không tìm thấy người dùng nào phù hợp với từ khóa "{searchTerm}"</div>
          </div>
        )}
      </div>

      {selectedUserId && (
        <UserActivitiesDialog
          userId={selectedUserId}
          userName={selectedUserName}
          startDateLocal={startDateLocal}
          endDate={endDate}
          isOpen={isDialogOpen}
          onClose={handleCloseDialog}
        />
      )}
    </div>
  );
};

export default TournamentUserRanking; 